import React, { useState, useEffect, useMemo } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import { addnewMapping, getAvailableStorageLocations, getAllMapping } from "../api/mapping";
import { useDebounce } from "../hooks/debounce";
import Dropdown from "../components/Dropdown";

const AddNewMapping = () => {
  const [formData, setFormData] = useState({
    emp_id: "",
    storage_location_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [availableStorageLocations, setAvailableStorageLocations] = useState<any[]>([]);
  const [loadingStorageLocations, setLoadingStorageLocations] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);

  const navigate = useNavigate();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | { target: { name: string; value: string | string[] } }
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Transform storage locations to dropdown format
  const storageLocationOptions = useMemo(() => {
    return availableStorageLocations.map((location) => {
      const id = location.id || location.storage_location_id || location;
      const locationName = location.name || location.storage_location_name || location.storage_location_id || String(location);
      const displayName = `${locationName} (${id})`;
      return {
        name: displayName,
        value: String(id),
      };
    });
  }, [availableStorageLocations]);

  const handleEmpIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const empId = e.target.value;
    setFormData({ ...formData, emp_id: empId });
    setEmployeeDetails(null);
  };

  const debouncedEmpId = useDebounce(formData.emp_id, 500);

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      // Only call API when emp_id is exactly 6 digits
      if (debouncedEmpId && debouncedEmpId.trim() !== "" && /^\d{6}$/.test(debouncedEmpId.trim())) {
        try {
          setLoadingEmployeeDetails(true);
          const response = await getAllMapping(1, 20, { search: debouncedEmpId.trim() });
          // Handle different response structures
          if (response) {
            if (Array.isArray(response)) {
              setEmployeeDetails(response.length > 0 ? response[0] : null);
            } else if (response.items && Array.isArray(response.items) && response.items.length > 0) {
              setEmployeeDetails(response.items[0]);
            } else if (response.emp_id) {
              // Direct object response
              setEmployeeDetails(response);
            } else {
              setEmployeeDetails(null);
            }
          } else {
            setEmployeeDetails(null);
          }
        } catch (error: any) {
          console.error("Failed to fetch employee details:", error);
          setEmployeeDetails(null);
        } finally {
          setLoadingEmployeeDetails(false);
        }
      } else {
        setEmployeeDetails(null);
      }
    };

    fetchEmployeeDetails();
  }, [debouncedEmpId]);

  // Clear storage_location_id when employee has existing mapping
  useEffect(() => {
    if (employeeDetails && employeeDetails.storage_location_id) {
      setFormData((prev) => ({ ...prev, storage_location_id: "" }));
    }
  }, [employeeDetails]);

  useEffect(() => {
    const fetchAvailableStorageLocations = async () => {
      try {
        setLoadingStorageLocations(true);
        const response = await getAvailableStorageLocations();
        if (response && Array.isArray(response)) {
          setAvailableStorageLocations(response);
        } else if (response && Array.isArray(response.items)) {
          setAvailableStorageLocations(response.items);
        } else if (response && Array.isArray(response.data)) {
          setAvailableStorageLocations(response.data);
        } else {
          setAvailableStorageLocations([]);
        }
      } catch (error: any) {
        console.error("Failed to fetch available storage locations:", error);
        toast.error("Failed to load available storage locations");
        setAvailableStorageLocations([]);
      } finally {
        setLoadingStorageLocations(false);
      }
    };

    fetchAvailableStorageLocations();
  }, []);

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${
      invalidFields.includes(field) ? "border-red-500" : ""
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      emp_id: formData.emp_id,
      storage_location_id: formData.storage_location_id,
    };

    try {
      setLoading(true);
      const emptyFields = Object.entries(formData)
        .filter(([_, value]) => value === null || value === "")
        .map(([key]) => key);

      setInvalidFields(emptyFields);

      if (emptyFields.length > 0) {
        toast.error("Please fill all required fields.");
        return;
      }

      const response = await addnewMapping(payload);
      if (!response) {
        toast.error("No data received or malformed response");
        console.warn("No data received or malformed response:", response);
        return;
      }
      toast.success(response?.message);
      setTimeout(() => {
        navigate(URL_MAPPING.STORAGEMAPPING);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to submit data:", error.response.data.detail);
      if (error?.response?.status === 400) {
        // toast.error(error.response.data.detail,{
        //   autoClose: false,
        //   closeOnClick: true,
        //   draggable: true,
        // });
      }
      if (error?.response?.status === 404) {
        toast.error("Route not found, Please try again later",{
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        });
      } else if (error?.response?.status === 500) {
        toast.error("Server error while submitting data",{
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        });
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) => toast.error(err.msg,{
            autoClose: false,
            closeOnClick: true,
            draggable: true,
          }));
        } else {
          toast.error(detail?.msg || detail || "An unexpected error occurred.",{
            autoClose: false,
            closeOnClick: true,
            draggable: true,
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayerAdmin>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            width="10"
            height="10"
            viewBox="0 0 256 256"
            xmlSpace="preserve"
          >
            <g
              style={{
                stroke: "none",
                strokeWidth: 0,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: "none",
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
            >
              <polygon
                points="20.48,45 59.75,90 69.52,81.48 37.69,45 69.52,8.52 59.75,0 "
                style={{
                  stroke: "none",
                  strokeWidth: 1,
                  strokeDasharray: "none",
                  strokeLinecap: "butt",
                  strokeLinejoin: "miter",
                  strokeMiterlimit: 10,
                  fill: "rgb(0,0,0)",
                  fillRule: "nonzero",
                  opacity: 1,
                }}
                transform="  matrix(1 0 0 1 0 0) "
              />
            </g>
          </svg>
        </button>
        <h3 className="text-xl font-bold text-gray-700">Add Mapping</h3>
      </div>
      <form onSubmit={handleSubmit} className="p-4 lg:w-1/3 sm:w-full">
        <div className="my-2">
          <label className="block font-semibold mb-1">Employee ID *</label>
          <input
            type="text"
            name="emp_id"
            value={formData.emp_id}
            onChange={handleEmpIdChange}
            className={getInputClass("emp_id")}
            placeholder="Employee Id"
          />
          {loadingEmployeeDetails && (
            <p className="text-sm text-gray-500 mt-1">Loading employee details...</p>
          )}
          {employeeDetails && !loadingEmployeeDetails && (
            <div className="mt-2 p-3 bg-gray-50 rounded border">
              <p className="text-sm font-semibold text-gray-700">Employee Details:</p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Name:</span> {employeeDetails.employee_name || "N/A"}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Storage Location ID:</span> {employeeDetails.storage_location_id || "N/A"}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Storage Location Name:</span> {employeeDetails.storage_location_name || "N/A"}
              </p>
            </div>
          )}
        </div>

        <div className="my-2">
          <label className="block font-semibold mb-1">
            Storage Location ID *
          </label>
          <Dropdown
            name="storage_location_id"
            options={storageLocationOptions}
            value={formData.storage_location_id}
            onChange={handleChange}
            searchable={true}
            placeholder="Select Storage Location"
            disabled={loadingStorageLocations || (employeeDetails && employeeDetails.storage_location_id)}
            className={`w-full ${invalidFields.includes("storage_location_id") ? "border-red-500" : "border-gray-300"}`}
          />
          {loadingStorageLocations && (
            <p className="text-sm text-gray-500 mt-1">Loading storage locations...</p>
          )}
          {employeeDetails && employeeDetails.storage_location_id && !loadingEmployeeDetails && (
            <p className="text-sm text-amber-600 mt-1">
              This Employee has an existing storage location please delete it to map a new storage location
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || (employeeDetails && employeeDetails.storage_location_id)}
          className={`px-6 py-2 rounded flex items-center gap-2 ${
            loading || (employeeDetails && employeeDetails.storage_location_id)
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-teal-500 text-white hover:bg-teal-600"
          }`}
        >
          Add Mapping {loading && <ButtonLoader />}
        </button>
      </form>
    </LayerAdmin>
  );
};

export default AddNewMapping;
